import React, { useEffect, useRef, useState, useMemo } from 'react';
import { missionData, captionsMap } from './missionData';
import { createPortal } from 'react-dom';

const CaptionDisplay = ({ caption }) => {
  if (!caption) return null;
  const [figLabel, ...rest] = caption.split(': ');
  const body = rest.join(': ');

  return (
    <div
      style={{
        position: 'absolute',
        bottom: '12%',
        left: '50%',
        transform: 'translateX(-50%)',
        color: '#ccc',
        backgroundColor: 'rgba(0,0,0,0.5)',
        padding: '0.5rem 1rem',
        borderRadius: 6,
        fontSize: '0.8rem',
        zIndex: 10001,
        textAlign: 'center',
        maxWidth: '80%',
      }}
    >
      <strong>{figLabel}:</strong> {body}
    </div>
  );
};

// Enlarged overlay for zooming and panning images
function EnlargedImageOverlay({ images, initialIndex = 0, onClose, imgCaptions = [] }) {
  const containerRef = useRef(null);
  const imgRef = useRef(null);

  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const lastTouch = useRef({ distance: null, midpoint: null });

  const src = images[currentIndex];

  const clampTranslation = (newTranslate, scale) => {
    const container = containerRef.current;
    const image = imgRef.current;

    if (!container || !image) return newTranslate;

    const containerRect = container.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;

    const baseWidth = image.offsetWidth;
    const baseHeight = image.offsetHeight;

    const imageWidth = baseWidth * scale;
    const imageHeight = baseHeight * scale;

    const margin = 20;

    const maxX = Math.max(0, (imageWidth - containerWidth) / 2 - margin);
    const maxY = Math.max(0, (imageHeight - containerHeight) / 2 - margin);

    return {
      x: Math.min(maxX, Math.max(-maxX, newTranslate.x)),
      y: Math.min(maxY, Math.max(-maxY, newTranslate.y)),
    };
  };


  useEffect(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
    setCurrentIndex(initialIndex);
  }, [images, initialIndex]);

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = -e.deltaY;
    const zoomIntensity = 0.001;

    setScale((prev) => {
      const newScale = Math.min(10, Math.max(1, prev + delta * zoomIntensity));
      if (newScale <= 1) setTranslate({ x: 0, y: 0 });
      else setTranslate((prevTranslate) => clampTranslation(prevTranslate, newScale));
      return newScale;
    });
  };

  const handleMouseDown = (e) => {
    if (scale <= 1) return;
    setIsDragging(true);
    setLastPos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e) => {
    if (!isDragging || scale <= 1) return;
      const dx = e.clientX - lastPos.x;
      const dy = e.clientY - lastPos.y;
      setTranslate((prev) => clampTranslation({ x: prev.x + dx, y: prev.y + dy }, scale));
      setLastPos({ x: e.clientX, y: e.clientY });
    };

  const handleMouseUp = () => setIsDragging(false);

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      const [a, b] = e.touches;
      const dx = a.clientX - b.clientX;
      const dy = a.clientY - b.clientY;
      const midpoint = {
        x: (a.clientX + b.clientX) / 2,
        y: (a.clientY + b.clientY) / 2,
      };
      lastTouch.current = {
        distance: Math.hypot(dx, dy),
        midpoint,
      };
    } else if (e.touches.length === 1) {
      setLastPos({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    }
  }

  // const handleTouchMove = (e) => {
  //   if (e.touches.length === 2) {
  //     const [a, b] = e.touches;
  //     const dx = a.clientX - b.clientX;
  //     const dy = a.clientY - b.clientY;
  //     const newDistance = Math.hypot(dx, dy);
  //     const delta = newDistance - lastTouch.current.distance;
  //     lastTouch.current.distance = newDistance;

  //     setScale((prev) => {
  //       const newScale = Math.min(10, Math.max(1, prev + delta * 0.005));
  //       if (newScale <= 1) setTranslate({ x: 0, y: 0 });
  //       else setTranslate((prevTranslate) => clampTranslation(prevTranslate, newScale));
  //       return newScale;
  //     });
  //   } else if (e.touches.length === 1 && scale > 1) {
  //     const dx = e.touches[0].clientX - lastPos.x;
  //     const dy = e.touches[0].clientY - lastPos.y;
  //     setTranslate((prev) => clampTranslation({ x: prev.x + dx, y: prev.y + dy }, scale));
  //     setLastPos({ x: e.touches[0].clientX, y: e.touches[0].clientY });
  //   }
  // };
  const handleTouchMove = (e) => {
    if (e.touches.length === 2) {
      const [a, b] = e.touches;
      const dx = a.clientX - b.clientX;
      const dy = a.clientY - b.clientY;
      const newDistance = Math.hypot(dx, dy);
      const midpoint = {
        x: (a.clientX + b.clientX) / 2,
        y: (a.clientY + b.clientY) / 2,
      };

      const prevDistance = lastTouch.current.distance ?? newDistance;
      const delta = newDistance - prevDistance;

      lastTouch.current.distance = newDistance;

      setScale((prevScale) => {
        let newScale = Math.min(10, Math.max(1, prevScale + delta * 0.005));
        newScale = Number(newScale.toFixed(4)); // avoid floating point noise

        const container = containerRef.current;
        if (!container) return newScale;

        const rect = container.getBoundingClientRect();
        const offsetX = midpoint.x - rect.left - rect.width / 2;
        const offsetY = midpoint.y - rect.top - rect.height / 2;

        setTranslate((prev) => {
          const scaleChange = newScale / prevScale;
          const newX = prev.x - offsetX * (scaleChange - 1);
          const newY = prev.y - offsetY * (scaleChange - 1);
          return clampTranslation({ x: newX, y: newY }, newScale);
        });

        return newScale;
      });
    } else if (e.touches.length === 1 && scale > 1) {
      const dx = e.touches[0].clientX - lastPos.x;
      const dy = e.touches[0].clientY - lastPos.y;
      setTranslate((prev) => clampTranslation({ x: prev.x + dx, y: prev.y + dy }, scale));
      setLastPos({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    }
    e.preventDefault();
  };


  useEffect(() => {
    const handleTouchEnd = () => {
      setIsDragging(false);

      setTranslate((prev) => clampTranslation(prev, scale));

      lastTouch.current = { distance: null, midpoint: null };
    };

    window.addEventListener('touchend', handleTouchEnd);
    window.addEventListener('touchcancel', handleTouchEnd);

    return () => {
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [scale]);


  return createPortal(
    <div
      ref={containerRef}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0,0,0,0.9)',
        zIndex: 10000,
        overflow: 'hidden',
        touchAction: 'none',
        // cursor: isDragging ? 'grabbing' : 'grab',
      }}
    >
      <div
        onClick={onClose}
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
      />

      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        style={{
          position: 'absolute',
          top: '14px',
          right: '20px',
          fontSize: '26px',
          background: 'transparent',
          border: 'none',
          color: '#bbb',
          cursor: 'pointer',
          zIndex: 10001,
        }}
        aria-label="Close overlay"
      >
        ×
      </button>

      {currentIndex > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setCurrentIndex((prev) => prev - 1);
            setScale(1);
            setTranslate({ x: 0, y: 0 });
          }}
          style={{
            position: 'absolute',
            left: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '32px',
            color: 'white',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            zIndex: 10001,
          }}
        >
          ‹
        </button>
      )}

      {currentIndex < Array.isArray(images) && currentIndex < images.length - 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setCurrentIndex((prev) => prev + 1);
            setScale(1);
            setTranslate({ x: 0, y: 0 });
          }}
          style={{
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '32px',
            color: 'white',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            zIndex: 10001,
          }}
        >
          ›
        </button>
      )}

      <div
        style={{
          position: 'absolute',
          bottom: '5rem',
          left: '50%',
          transform: 'translateX(-50%)',
          color: '#ccc',
          fontSize: '16px',
          zIndex: 10001,
        }}
      >
        {currentIndex + 1} / {images.length}
      </div>

      <div
        style={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <img
          ref={imgRef}
          src={src}
          alt="Zoomable mission visual"
          onClick={(e) => e.stopPropagation()}
          style={{
            transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
            transition: isDragging ? 'none' : 'transform 0.2s ease-out',
            objectFit: 'contain',
            borderRadius: 8,
            userSelect: 'none',
            touchAction: 'none',
            maxWidth: '90vw',
            maxHeight: '90vh',
          }}
          draggable={false}
        />
        {scale === 1 && imgCaptions[currentIndex] && (
          <CaptionDisplay caption={`${imgCaptions[currentIndex]}`} />
        )}
      </div>
    </div>,
    document.body
  );
}

// ⬇ New reusable component
function MissionImages({ imageKeys = [], assets }) {
  const [enlarged, setEnlarged] = useState(null);
  const closeEnlarged = () => setEnlarged(null);

  const imagesWithCaptions = useMemo(() => {
    return imageKeys
      .map((key) => {
        const src = assets?.[key]?.src ?? null;
        const caption = captionsMap?.[key] ?? null;
        return src ? { src, caption } : null;
      })
      .filter(Boolean);
  }, [imageKeys, assets]);

  if (!imagesWithCaptions.length) return null;

  return (
    <>
      <div
        style={{
          marginTop: '0.5rem',
          display: 'flex',
          gap: '1rem',
          flexWrap: 'wrap',
        }}
      >
        {imagesWithCaptions.map((item, i) => (
          <button
            key={i}
            onClick={() =>
              setEnlarged({
                images: imagesWithCaptions.map((img) => img.src),
                index: i,
                captions: imagesWithCaptions.map((img) => img.caption),
              })
            }
            style={{
              padding: 0,
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              flex: '1 0 48%',
              maxWidth: '48%',
              aspectRatio: '4 / 3',
              overflow: 'hidden',
              borderRadius: 4,
            }}
            aria-label={`Enlarge image ${i + 1}`}
          >
            <img
              src={item.src}
              alt="Mission visual"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          </button>
        ))}
      </div>
      {enlarged && (
        <EnlargedImageOverlay
          images={enlarged.images}
          initialIndex={enlarged.index}
          onClose={closeEnlarged}
          imgCaptions={enlarged.captions}
        />
      )}
    </>
  );
}

// Factory function to generate per-planet components
export default function planetMissions(assets = {}) {
  function MissionsForPlanet({ planet }) {
    const missions = missionData[planet] || [];

    const renderMission = (mission, index) => (
      <div key={index} style={{ marginBottom: '1rem' }}>
        <strong>
          {mission.name} ({mission.year}) ({mission.status}):
        </strong>{' '}
        {mission.description}
        <br />
        <strong><em>Importance:</em></strong> {mission.importance}
        <br />
        <strong><em>Speed:</em></strong> {mission.speed} | <em>Distance:</em> {mission.distance}
        <br />
        <strong><em>Cost:</em></strong> {mission.cost}
        <MissionImages imageKeys={mission.images} assets={assets} />
      </div>
    );

    return <>{missions.map(renderMission)}</>;
  }

  const result = {};
  for (const planet in missionData) {
    result[planet] = <MissionsForPlanet key={planet} planet={planet} />;
  }
  return result;
}
